import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

export const BannerSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'Empowering Digital Innovation',
      description: 'Max Bien Software delivers futuristic solutions with forensic precision. From immersive dashboards to AI-powered automation, we build experiences that captivate and perform.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      buttons: [
        { text: 'Explore Services', link: '/services' },
        { text: 'Request Demo', link: '/contact' }
      ]
    },
    {
      title: 'Cutting-Edge Web Development',
      description: 'We create responsive, scalable, and blazing-fast websites that deliver exceptional user experiences across all devices.',
      image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80',
      buttons: [
        { text: 'View Portfolio', link: '/portfolio' },
        { text: 'Get Quote', link: '/contact' }
      ]
    },
    {
      title: 'AI-Powered Solutions',
      description: 'Leverage the power of artificial intelligence to automate processes, gain insights, and deliver personalized experiences to your customers.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      buttons: [
        { text: 'AI Services', link: '/services' },
        { text: 'Case Studies', link: '/case-studies' }
      ]
    },
    {
      title: 'Cybersecurity Excellence',
      description: 'Protect your digital assets with our forensic-grade security solutions designed to withstand modern threats.',
      image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      buttons: [
        { text: 'Security Audit', link: '/contact' },
        { text: 'Learn More', link: '/services' }
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative h-[80vh] overflow-hidden">
      <div
        className="flex h-full transition-transform duration-1000 ease-in-out"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className="min-w-full h-full flex items-center justify-center relative"
            style={{
              backgroundImage: `linear-gradient(rgba(10, 37, 64, 0.7), rgba(10, 37, 64, 0.7)), url('${slide.image}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="container mx-auto px-6 text-center z-10 animate-slide-up">
              <h1
                className="text-5xl md:text-7xl font-bold mb-6 led-text tracking-wider"
                style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}
              >
                {slide.title}
              </h1>
              <p
                className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-white"
                style={{ textShadow: '0 0 10px #00a8ff' }}
              >
                {slide.description}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                {slide.buttons.map((button, btnIdx) => (
                  <Link key={btnIdx} to={button.link}>
                    <Button
                      className="bg-transparent border-2 border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold px-8 py-6 text-lg transition-all duration-300 relative overflow-hidden group"
                      style={{ 
                        boxShadow: '0 0 15px #00a8ff',
                        textShadow: '0 0 8px #00a8ff'
                      }}
                    >
                      <span className="relative z-10">{button.text}</span>
                      <span className="absolute inset-0 bg-[#00a8ff] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-in-out"></span>
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-4 h-4 rounded-full transition-all duration-300 ${
              currentSlide === index
                ? 'bg-[#00a8ff] scale-125'
                : 'bg-white/50'
            }`}
            style={currentSlide === index ? { boxShadow: '0 0 15px #00a8ff' } : {}}
          />
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-8 top-1/2 transform -translate-y-1/2 w-14 h-14 rounded-full bg-black/30 flex items-center justify-center text-[#00a8ff] hover:bg-[rgba(0,168,255,0.2)] transition-all duration-300 z-20 opacity-70 hover:opacity-100 hover:scale-110"
        style={{ textShadow: '0 0 15px #00a8ff' }}
      >
        <ChevronLeft size={32} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-8 top-1/2 transform -translate-y-1/2 w-14 h-14 rounded-full bg-black/30 flex items-center justify-center text-[#00a8ff] hover:bg-[rgba(0,168,255,0.2)] transition-all duration-300 z-20 opacity-70 hover:opacity-100 hover:scale-110"
        style={{ textShadow: '0 0 15px #00a8ff' }}
      >
        <ChevronRight size={32} />
      </button>
    </section>
  );
};
